import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';
import { useStickyScroll } from '@aimsos/hooks';
import {
  DashboardBuilderCard,
  DashboardListHeader,
  DashboardCardSkeleton,
  DashboardListTabs,
  TemplateFilters,
  TemplatePreviewModal,
} from '../components';
import { SKELETON_ITEMS } from '../constants';
import { useDashboardData, useDashboardUI } from '../context';

/**
 * Displays pre-configured dashboard templates in a card grid.
 * Tab: Templates (/dashboards/templates)
 */
const DashboardTemplatesPage = () => {
  const { t } = useTranslation('dashboards');
  const navigate = useNavigate();
  const toast = useToast();
  const { templates, loadingTemplates, createDashboard } = useDashboardData();
  const { templateFilters } = useDashboardUI();
  const hasActiveFilters = Boolean(templateFilters.search || templateFilters.sectionId || templateFilters.categoryId);
  const isEmpty = !loadingTemplates && templates.length === 0 && !hasActiveFilters;

  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [installing, setInstalling] = useState(false);

  const handleClosePreview = useCallback(() => {
    if (installing) return;
    setPreviewTemplate(null);
  }, [installing]);

  const handleInstall = useCallback(
    async (template) => {
      setInstalling(true);
      try {
        const created = await createDashboard({
          title: template.title,
          description: template.description,
          sectionId: template.sectionId,
          categoryKeys: template.categoryKeys,
          templateId: template.id,
        });
        setPreviewTemplate(null);
        toast.success({ title: t('templatePreview.toasts.installed', { name: template.title }), canClose: true });
        navigate(`/dashboards/${created.id}`);
      } catch {
        toast.error({
          title: t('templatePreview.toasts.installError', { name: template.title }),
          message: t('templatePreview.toasts.installErrorDescription'),
          canClose: true,
        });
      } finally {
        setInstalling(false);
      }
    },
    [createDashboard, navigate, t, toast],
  );

  const scrollRef = useRef(null);
  const { isScrolled, filtersVisible, onMouseEnter, onMouseLeave } = useStickyScroll(scrollRef);
  const showToolbar = !isScrolled || filtersVisible;

  return (
    <main ref={scrollRef} className="scrollbar-aimsos-auto flex-1 overflow-y-auto bg-surface-base">
      <div className="flex w-full flex-col gap-6 px-6 pb-6">
        {/* Same sticky bar as the Created tab — switching tabs must not change
            how the page scrolls. */}
        {/* Same bar as the Created tab — switching tabs must not change how the
            page scrolls. */}
        <div
          className="sticky top-0 z-20 -mx-6 px-6"
          onMouseEnter={isScrolled ? onMouseEnter : undefined}
          onMouseLeave={isScrolled ? onMouseLeave : undefined}
        >
          <div
            className={[
              'pt-6 transition-colors duration-200',
              isScrolled ? 'bg-surface-base' : '',
            ].filter(Boolean).join(' ')}
          >
            <DashboardListHeader isScrolled={isScrolled} />

            <div
              inert={isScrolled ? '' : undefined}
              className={[
                'overflow-hidden transition-all duration-200',
                isScrolled ? 'mt-0 max-h-0 opacity-0' : 'mt-6 max-h-20 opacity-100',
              ].join(' ')}
            >
              <DashboardListTabs />
            </div>

            {!isEmpty ? (
              <div
                inert={showToolbar ? undefined : ''}
                className={[
                  'overflow-hidden transition-all duration-200',
                  showToolbar ? 'mt-6 max-h-40 opacity-100' : 'mt-0 max-h-0 opacity-0',
                ].join(' ')}
              >
                <TemplateFilters />
              </div>
            ) : null}
          </div>

          {isScrolled ? (
            <div
              aria-hidden
              className="pointer-events-none h-10 bg-gradient-to-b from-surface-base to-transparent"
            />
          ) : null}
        </div>

        <section className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {loadingTemplates
            ? <DashboardCardSkeleton count={SKELETON_ITEMS.length} />
            : isEmpty
              ? (
                  <div className="col-span-full rounded-lg border border-edge bg-surface px-6 py-8 text-center">
                    <h2 className="text-lg font-semibold text-content">{t('templates.emptyState.title')}</h2>
                    <p className="mt-2 text-sm text-content-muted">{t('templates.emptyState.description')}</p>
                  </div>
                )
              : templates.map((template) => (
                  <DashboardBuilderCard
                    key={template.id}
                    dashboard={template}
                    onPreview={setPreviewTemplate}
                  />
                ))}
        </section>
      </div>

      <TemplatePreviewModal
        open={Boolean(previewTemplate)}
        template={previewTemplate}
        onClose={handleClosePreview}
        onInstall={handleInstall}
        installing={installing}
      />
    </main>
  );
};

export default DashboardTemplatesPage;
